import type { UserProfile } from '../types';

/** Role-specific fields stored on `coach_profiles` (not `profiles` long term). */
export const COACH_PROFILE_FIELD_KEYS = [
  'institution',
  'coach_title',
  'coach_verified',
  'coaching_certifications',
] as const;

/** Role-specific fields stored on `parent_profiles`. */
export const PARENT_PROFILE_FIELD_KEYS = [
  'child_name',
  'child_grad_year',
  'child_high_school',
  'relationship',
  'stripe_identity_verification_session_id',
  'stripe_identity_status',
  'stripe_identity_verified_at',
  'stripe_identity_last_error',
] as const;

export type CoachProfileFieldKey = (typeof COACH_PROFILE_FIELD_KEYS)[number];
export type ParentProfileFieldKey = (typeof PARENT_PROFILE_FIELD_KEYS)[number];

const coachKeySet = new Set<string>(COACH_PROFILE_FIELD_KEYS);
const parentKeySet = new Set<string>(PARENT_PROFILE_FIELD_KEYS);

export function pickCoachProfileUpdates(
  updates: Partial<UserProfile>
): Partial<Record<CoachProfileFieldKey, unknown>> {
  const out: Partial<Record<CoachProfileFieldKey, unknown>> = {};
  for (const key of COACH_PROFILE_FIELD_KEYS) {
    if (key in updates) {
      out[key] = updates[key as keyof UserProfile];
    }
  }
  return out;
}

export function pickParentProfileUpdates(
  updates: Partial<UserProfile>
): Partial<Record<ParentProfileFieldKey, unknown>> {
  const out: Partial<Record<ParentProfileFieldKey, unknown>> = {};
  for (const key of PARENT_PROFILE_FIELD_KEYS) {
    if (key in updates) {
      out[key] = updates[key as keyof UserProfile];
    }
  }
  return out;
}

export function pickCoreProfileUpdates(updates: Partial<UserProfile>): Partial<UserProfile> {
  const out: Partial<UserProfile> = { ...updates };
  for (const key of [...COACH_PROFILE_FIELD_KEYS, ...PARENT_PROFILE_FIELD_KEYS]) {
    delete (out as Record<string, unknown>)[key];
  }
  return out;
}

function extensionValue<T>(row: Record<string, unknown>, key: string): T | undefined {
  if (!(key in row)) return undefined;
  const value = row[key];
  if (value === null || value === undefined) return undefined;
  return value as T;
}

export function mergeCoachFieldsIntoProfile(
  profile: UserProfile,
  coachRow: Record<string, unknown> | null | undefined
): UserProfile {
  if (!coachRow) return profile;
  const merged = { ...profile };
  for (const key of COACH_PROFILE_FIELD_KEYS) {
    const ext = extensionValue<unknown>(coachRow, key);
    if (ext !== undefined) {
      (merged as Record<string, unknown>)[key] = ext;
    }
  }
  return merged;
}

export function mergeParentFieldsIntoProfile(
  profile: UserProfile,
  parentRow: Record<string, unknown> | null | undefined
): UserProfile {
  if (!parentRow) return profile;
  const merged = { ...profile };
  for (const key of PARENT_PROFILE_FIELD_KEYS) {
    const ext = extensionValue<unknown>(parentRow, key);
    if (ext !== undefined) {
      (merged as Record<string, unknown>)[key] = ext;
    }
  }
  return merged;
}

export function isCoachProfileField(key: string): boolean {
  return coachKeySet.has(key);
}

export function isParentProfileField(key: string): boolean {
  return parentKeySet.has(key);
}
